import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  BadgeCheck,
  BriefcaseBusiness,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  PhoneCall,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { PARTNER_SPECIALTY_OPTIONS, registerPartner } from "../../utils/partnerApi";
import "./PartnerRegister.css";

const initialForm = {
  prenom: "",
  nom: "",
  email: "",
  password: "",
  telephone: "",
  city: "",
  companyName: "",
  businessType: "",
  selfEmployed: "true",
  siret: "",
  yearsExperience: "",
  currentProfession: "",
  description: "",
  workRadiusKm: "30",
  availableDays: "",
  previousEmployers: "",
  currentActivity: "",
  previousClients: "",
  toolsEquipment: "",
  experienceLevel: "",
};

export default function PartnerRegisterPage() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [specialties, setSpecialties] = useState([]);
  const [files, setFiles] = useState({
    profilePhoto: null,
    identityDocument: null,
    siretProof: null,
    insuranceDocument: null,
    certifications: [],
    portfolio: [],
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const totalSteps = 5;

  const toggleSpec = (key) => {
    setSpecialties((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const setFile = (key, e) => {
    const f = e.target.files?.[0];
    setFiles((prev) => ({ ...prev, [key]: f || null }));
  };

  const setMultiFiles = (key, e) => {
    const list = Array.from(e.target.files || []);
    setFiles((prev) => ({ ...prev, [key]: list }));
  };

  const canNext = useMemo(() => {
    if (step === 1) {
      return (
        form.prenom.trim() &&
        form.nom.trim() &&
        form.email.trim() &&
        form.password.length >= 8 &&
        form.telephone.trim() &&
        form.city.trim()
      );
    }
    if (step === 2) {
      return (
        specialties.length > 0 &&
        form.description.trim().length >= 20 &&
        form.currentProfession.trim()
      );
    }
    if (step === 3) {
      return (
        !!files.identityDocument &&
        !!files.siretProof &&
        !!files.insuranceDocument &&
        /^\d{14}$/.test(form.siret.replace(/\s/g, ""))
      );
    }
    if (step === 4) {
      return form.currentActivity.trim().length >= 5;
    }
    return true;
  }, [step, form, specialties, files]);

  const next = () => {
    setErr("");
    if (!canNext) {
      setErr("Complétez les champs obligatoires avant de continuer.");
      return;
    }
    setStep((s) => Math.min(totalSteps, s + 1));
  };

  const back = () => {
    setErr("");
    setStep((s) => Math.max(1, s - 1));
  };

  const submit = async () => {
    setErr("");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("prenom", form.prenom.trim());
      fd.append("nom", form.nom.trim());
      fd.append("email", form.email.trim().toLowerCase());
      fd.append("password", form.password);
      fd.append("telephone", form.telephone.trim());
      fd.append("city", form.city.trim());
      fd.append("companyName", form.companyName.trim());
      fd.append("businessType", form.businessType.trim());
      fd.append("selfEmployed", form.selfEmployed);
      fd.append("siret", form.siret.replace(/\s/g, ""));
      fd.append("yearsExperience", String(form.yearsExperience || "0"));
      fd.append("currentProfession", form.currentProfession.trim());
      fd.append("description", form.description.trim());
      fd.append("workRadiusKm", form.workRadiusKm);
      fd.append("availableDays", form.availableDays.trim());
      fd.append("requestedSpecialties", JSON.stringify(specialties));
      fd.append("previousEmployers", form.previousEmployers.trim());
      fd.append("currentActivity", form.currentActivity.trim());
      fd.append("previousClients", form.previousClients.trim());
      fd.append("toolsEquipment", form.toolsEquipment.trim());
      fd.append("experienceLevel", form.experienceLevel.trim());

      if (files.profilePhoto) fd.append("profilePhoto", files.profilePhoto);
      if (files.identityDocument) fd.append("identityDocument", files.identityDocument);
      if (files.siretProof) fd.append("siretProof", files.siretProof);
      if (files.insuranceDocument) fd.append("insuranceDocument", files.insuranceDocument);
      (files.certifications || []).forEach((f) => fd.append("certifications", f));
      (files.portfolio || []).forEach((f) => fd.append("portfolio", f));

      await registerPartner(fd);
      setDone(true);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Erreur lors de l'envoi du dossier.";
      setErr(String(msg));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="pp-shell">
        <div className="pp-card">
          <div className="pp-success">
            <ShieldCheck size={48} color="var(--color-primary-hover)" style={{ marginBottom: 12 }} />
            <h2>Dossier transmis</h2>
            <p className="pp-sub" style={{ marginBottom: 0 }}>
              Votre candidature <strong>ProMeet Partners</strong> est en cours d’analyse.
              Un entretien téléphonique ou visio pourra être planifié avant validation.
              Vérifiez votre boîte mail pour confirmer votre adresse email, puis connectez-vous
              pour suivre l’avancement.
            </p>
            <div className="pp-actions" style={{ justifyContent: "center", marginTop: 24 }}>
              <Link
                className="pp-btn pp-btn--primary"
                to="/auth/verify-email"
                state={{ email: form.email.trim().toLowerCase(), startCooldown: true }}
              >
                Vérifier mon email <ChevronRight size={16} />
              </Link>
              <Link className="pp-btn" to="/auth/login">
                Connexion
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pp-shell">
      <section className="pp-hero">
        <div className="pp-hero__badge">
          <Sparkles size={16} />
          ProMeet Partners
        </div>
        <h1>Devenez prestataire partenaire ProMeetService</h1>
        <p>
          Le compte Partner est réservé aux indépendants et professionnels automobile qui veulent
          recevoir des missions qualifiées : nettoyage, convoyage, préparation véhicule,
          carrosserie légère, jantes, polissage et autres interventions terrain.
        </p>
        <div className="pp-hero__cards">
          <div>
            <ClipboardCheck size={20} />
            <strong>Dossier vérifié</strong>
            <span>Vous envoyez vos infos, documents, diplômes ou photos de réalisations.</span>
          </div>
          <div>
            <PhoneCall size={20} />
            <strong>Call avec l’équipe</strong>
            <span>L’admin vérifie votre profil et vos spécialités avant activation.</span>
          </div>
          <div>
            <BadgeCheck size={20} />
            <strong>Accès par compétence</strong>
            <span>Vous ne voyez que les missions liées aux spécialités validées.</span>
          </div>
          <div>
            <BriefcaseBusiness size={20} />
            <strong>Missions attribuées</strong>
            <span>Vous postulez, puis l’admin attribue la mission au prestataire choisi.</span>
          </div>
        </div>
      </section>

      <div className="pp-card">
        <div className="pp-form-kicker">
          <ShieldCheck size={20} />
          Candidature prestataire
        </div>
        <h1 className="pp-title">Rejoindre le réseau partenaires</h1>
        <p className="pp-sub">
          Remplissez ce dossier pour demander l’accès à <strong>ProMeet Missions</strong>.
          Après validation, vous pourrez postuler uniquement sur les catégories que l’admin approuve.
        </p>

        <div className="pp-steps">
          {Array.from({ length: totalSteps }, (_, i) => (
            <div key={i} className={`pp-step-dot ${i < step ? "is-on" : ""}`} />
          ))}
        </div>

        {err && <div className="pp-err">{err}</div>}

        {step === 1 && (
          <>
            <h2 className="pp-title" style={{ fontSize: "1.15rem" }}>Étape 1 — Identité</h2>
            <div className="pp-grid">
              <div className="pp-field">
                <label>Prénom *</label>
                <input value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} />
              </div>
              <div className="pp-field">
                <label>Nom *</label>
                <input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} />
              </div>
              <div className="pp-field" style={{ gridColumn: "1 / -1" }}>
                <label>Email *</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="pp-field" style={{ gridColumn: "1 / -1" }}>
                <label>Mot de passe * (min. 8 caractères)</label>
                <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
              <div className="pp-field">
                <label>Téléphone *</label>
                <input value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} />
              </div>
              <div className="pp-field">
                <label>Ville *</label>
                <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              </div>
              <div className="pp-field" style={{ gridColumn: "1 / -1" }}>
                <label>Photo de profil / logo</label>
                <input type="file" accept="image/*,.pdf" onChange={(e) => setFile("profilePhoto", e)} />
              </div>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <h2 className="pp-title" style={{ fontSize: "1.15rem" }}>Étape 2 — Activité pro</h2>
            <div className="pp-grid">
              <div className="pp-field" style={{ gridColumn: "1 / -1" }}>
                <label>Raison sociale (optionnel)</label>
                <input value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })} />
              </div>
              <div className="pp-field">
                <label>Type d’activité</label>
                <input value={form.businessType} onChange={(e) => setForm({ ...form, businessType: e.target.value })} placeholder="ex: Auto-entrepreneur" />
              </div>
              <div className="pp-field">
                <label>Auto-entrepreneur / indépendant</label>
                <select value={form.selfEmployed} onChange={(e) => setForm({ ...form, selfEmployed: e.target.value })}>
                  <option value="true">Oui</option>
                  <option value="false">Non</option>
                </select>
              </div>
              <div className="pp-field">
                <label>SIRET * (14 chiffres — figurant sur le Kbis)</label>
                <input value={form.siret} onChange={(e) => setForm({ ...form, siret: e.target.value })} placeholder="12345678901234" />
              </div>
              <div className="pp-field">
                <label>Années d’expérience</label>
                <input type="number" min={0} max={60} value={form.yearsExperience} onChange={(e) => setForm({ ...form, yearsExperience: e.target.value })} />
              </div>
              <div className="pp-field" style={{ gridColumn: "1 / -1" }}>
                <label>Métier actuel *</label>
                <input value={form.currentProfession} onChange={(e) => setForm({ ...form, currentProfession: e.target.value })} />
              </div>
              <div className="pp-field" style={{ gridColumn: "1 / -1" }}>
                <label>Description * (min. 20 caractères)</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="pp-field">
                <label>Rayon d’intervention</label>
                <select value={form.workRadiusKm} onChange={(e) => setForm({ ...form, workRadiusKm: e.target.value })}>
                  <option value="10">10 km</option>
                  <option value="30">30 km</option>
                  <option value="50">50 km</option>
                  <option value="100">100 km</option>
                </select>
              </div>
              <div className="pp-field">
                <label>Jours disponibles (texte libre)</label>
                <input value={form.availableDays} onChange={(e) => setForm({ ...form, availableDays: e.target.value })} placeholder="ex: lun–ven" />
              </div>
            </div>
            <div className="pp-field" style={{ marginTop: 16 }}>
              <label>Spécialités demandées *</label>
              <div className="pp-spec">
                {PARTNER_SPECIALTY_OPTIONS.map((s) => (
                  <label key={s.key}>
                    <input
                      type="checkbox"
                      checked={specialties.includes(s.key)}
                      onChange={() => toggleSpec(s.key)}
                    />
                    {s.label}
                  </label>
                ))}
              </div>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <h2 className="pp-title" style={{ fontSize: "1.15rem" }}>Étape 3 — Documents obligatoires</h2>
            <p className="pp-sub" style={{ marginBottom: 12 }}>
              Ces pièces sont transmises à l&apos;équipe ProMeet pour vérification avant activation de votre compte partenaire.
            </p>
            <div className="pp-field">
              <label>Pièce d’identité *</label>
              <input type="file" accept="image/*,.pdf" onChange={(e) => setFile("identityDocument", e)} />
            </div>
            <div className="pp-field">
              <label>Extrait Kbis / justificatif SIRET * (≤ 3 mois)</label>
              <input type="file" accept="image/*,.pdf" onChange={(e) => setFile("siretProof", e)} />
            </div>
            <div className="pp-field">
              <label>Attestation assurance RC professionnelle *</label>
              <input type="file" accept="image/*,.pdf" onChange={(e) => setFile("insuranceDocument", e)} />
            </div>
            <div className="pp-field">
              <label>Certifications / diplômes (plusieurs fichiers)</label>
              <input type="file" multiple accept="image/*,.pdf" onChange={(e) => setMultiFiles("certifications", e)} />
            </div>
            <div className="pp-field">
              <label>Portfolio photos avant/après</label>
              <input type="file" multiple accept="image/*" onChange={(e) => setMultiFiles("portfolio", e)} />
            </div>
          </>
        )}

        {step === 4 && (
          <>
            <h2 className="pp-title" style={{ fontSize: "1.15rem" }}>Étape 4 — Expérience</h2>
            <div className="pp-field">
              <label>Employeurs / structures précédentes</label>
              <textarea value={form.previousEmployers} onChange={(e) => setForm({ ...form, previousEmployers: e.target.value })} />
            </div>
            <div className="pp-field">
              <label>Activité actuelle *</label>
              <textarea value={form.currentActivity} onChange={(e) => setForm({ ...form, currentActivity: e.target.value })} />
            </div>
            <div className="pp-field">
              <label>Références clients (si possible)</label>
              <textarea value={form.previousClients} onChange={(e) => setForm({ ...form, previousClients: e.target.value })} />
            </div>
            <div className="pp-field">
              <label>Équipement / outillage principal</label>
              <textarea value={form.toolsEquipment} onChange={(e) => setForm({ ...form, toolsEquipment: e.target.value })} />
            </div>
            <div className="pp-field">
              <label>Niveau d’expérience</label>
              <input value={form.experienceLevel} onChange={(e) => setForm({ ...form, experienceLevel: e.target.value })} placeholder="ex: 10 ans carrosserie" />
            </div>
          </>
        )}

        {step === 5 && (
          <>
            <h2 className="pp-title" style={{ fontSize: "1.15rem" }}>Étape 5 — Validation</h2>
            <p className="pp-sub">
              En soumettant ce dossier, vos informations et documents (identité, Kbis, RC pro) sont envoyés
              aux administrateurs ProMeetService pour vérification. Un <strong>entretien téléphonique ou visio</strong> pourra
              être requis avant validation. Vous ne pourrez accéder à ProMeet Missions qu’après approbation de vos
              spécialités par l’admin.
            </p>
          </>
        )}

        <div className="pp-actions">
          <button type="button" className="pp-btn" onClick={back} disabled={step === 1 || busy}>
            <ChevronLeft size={16} /> Retour
          </button>
          {step < totalSteps ? (
            <button type="button" className="pp-btn pp-btn--primary" onClick={next} disabled={busy}>
              Suivant <ChevronRight size={16} />
            </button>
          ) : (
            <button type="button" className="pp-btn pp-btn--primary" onClick={submit} disabled={busy}>
              {busy ? "Envoi…" : "Soumettre mon dossier"}
            </button>
          )}
        </div>

        <p style={{ marginTop: 20, fontSize: 13, color: "var(--text-muted)", textAlign: "center" }}>
          Déjà un compte ?{" "}
          <Link to="/auth/login" style={{ color: "var(--color-primary-hover)", fontWeight: 700 }}>
            Connexion
          </Link>
        </p>
        <p style={{ fontSize: 13, color: "var(--text-muted)", textAlign: "center" }}>
          <Link to="/" style={{ color: "var(--text-muted)" }}>← Retour à l’accueil</Link>
        </p>
      </div>
    </div>
  );
}
